import { RefObject, useEffect, useRef } from "react";

import { composedPathContains } from "./getComposedEventTarget";

type OpenPanel = { close: () => void };

const openPanels = new Set<OpenPanel>();

/**
 * Closes a floating panel when a click lands outside `ref` while it is open.
 *
 * The listener sits on `window`, so it tests containment through `composedPathContains`:
 * inside the Chrome extension's shadow root a window listener sees `event.target` retargeted
 * to the shadow host, and a raw containment check would read every click inside the panel as
 * outside (PREC-4296, arch rule `outside-click-composed-target`). `composedPathContains` also
 * sees a click on content the panel renders inside its own shadow root (PREC-4449), which
 * `ref.current.contains(getComposedEventTarget(event))` could not.
 *
 * The listener stays attached while closed and checks `isOpen` itself, as the blocks it
 * replaced did, so the click that opens the panel cannot also close it.
 *
 * A trigger that opens one panel usually stops the click's propagation so the grid row behind
 * it does not react, which means the window listener never sees that click and an already open
 * panel would stay open. So the hook also keeps a module-level registry of the panels currently
 * open: registering a panel closes the ones that were already open (PREC-5020). This is the
 * same outcome an outside click produces where the trigger does not stop propagation.
 *
 * The current `setIsOpen` is kept in a ref, so a registry close calls the setter of the latest
 * render even when the consumer passes a new setter identity each time. The registration effect
 * depends on `isOpen` only, so an open panel does not re-register on every re-render.
 */
export const useCloseOnOutsideClick = (
	ref: RefObject<HTMLElement | null>,
	isOpen: boolean,
	setIsOpen: (open: boolean) => void
): void => {
	const setIsOpenRef = useRef(setIsOpen);

	useEffect(() => {
		setIsOpenRef.current = setIsOpen;
	});

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			if (isOpen && !composedPathContains(ref.current, event)) {
				setIsOpen(false);
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [ref, isOpen, setIsOpen]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const entry: OpenPanel = { close: () => setIsOpenRef.current(false) };

		for (const openPanel of Array.from(openPanels)) {
			openPanel.close();
		}

		openPanels.add(entry);

		return () => {
			openPanels.delete(entry);
		};
	}, [isOpen]);
};
